<template>
  <div>
    <Header />

    <!-- 麵包屑導覽 -->
    <nav aria-label="breadcrumb" class="breadcrumb_box">
      <div class="container">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/Profile" title="首頁">Home</router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">
            International Student Application System
          </li>
        </ol>
      </div>
    </nav>

    <!-- 主要內容區 -->
    <div class="main form form_2">
      <!-- 無障礙快捷鍵 -->
      <a accesskey="c" class="accesskey" id="accesskey_c" href="javascript:void(0);" title="中央內容區塊"
        onclick="document.getElementById('accesskey_c').scrollIntoView();">
        :::
      </a>

      <div class="container">
        <div class="box" :class="{ 'submitted-readonly': isSubmitted }">
          <!-- 頁面標題與說明按鈕 -->
          <div class="title">
            <h2>International Student Application System</h2>
            <div class="btn_box">
              <button class="tours_btn" @click="startTour">
                <svg width="28" height="28" viewBox="0 0 28 28">
                  <defs>
                    <clipPath id="clip-path">
                      <rect id="_3" data-name="3" width="28" height="28" transform="translate(5575 10526)" fill="none"
                        stroke="#707070" stroke-width="1" />
                    </clipPath>
                  </defs>
                  <g id="icon_instructions" transform="translate(-5575 -10526)" clip-path="url(#clip-path)">
                    <g id="_2" data-name="2" transform="translate(5575.14 10526.5)">
                      <path id="_1" data-name="1"
                        d="M23.14,21.36a1,1,0,0,0,1-1V3.42A1.929,1.929,0,0,0,22.21,1.5H8.69V19.358h-2V1.5H5.93A3.077,3.077,0,0,0,2.86,4.57V22.43c0,.011.006.021.007.032A3.069,3.069,0,0,0,5.934,25.5h17.2a1,1,0,0,0,0-2H5.934a1.07,1.07,0,0,1,0-2.14H23.14Zm-7.751-15a1.007,1.007,0,0,1,1.021,1,1,1,0,0,1-2,0,1,1,0,0,1,.979-1ZM14.41,11.19a1,1,0,0,1,2,0v3.96a1,1,0,0,1-2,0Z"
                        fill="#580d63" />
                    </g>
                  </g>
                </svg>
                Instructions
              </button>
            </div>
          </div>

          <!-- 進度條 -->
          <ul class="progress_bar">
            <li v-for="(item, index) in progressItems" :key="index">
              <router-link :to="item.to" :class="['step_box', getStepClass(index)]">
                <div class="step">
                  <p v-if="index != 6">Step {{ index + 1 }}</p>
                  <p v-else>Payment</p>
                  <p>{{ item.title }}</p>
                </div>
                <div class="line"></div>
              </router-link>
            </li>
          </ul>

          <!-- 已提交提示訊息 -->
          <div v-if="isSubmitted" class="bg" style="background-color: #fff3cd; border-left: 4px solid #ffc107; padding: 15px 20px; margin-bottom: 20px;">
            <p style="margin: 0; color: #856404; font-weight: bold; font-size: 16px;">
              ⚠️ Application submitted and under review. View-only mode.
            </p>
          </div>

          <!-- 申請資訊說明區塊 -->
          <div class="bg">
            <div class="title_box description">
              <h3>{{ stepContent.title }}</h3>
            </div>
            <div v-if="stepContent.content" class="text_box" id="step1" v-html="stepContent.content"></div>
          </div>

          <!-- 載入指示器 -->
          <div v-if="isLoading" class="loading-container" style="text-align: center; padding: 40px;">
            <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
              <span class="sr-only">Loading...</span>
            </div>
            <p style="margin-top: 20px; font-size: 16px;">Loading form data...</p>
          </div>

          <!-- 系所選擇與列表區塊 -->
          <div v-else class="bg">
            <div class="title_box">
              <h3>Application Information</h3>
            </div>

            <!-- 新增申請系所按鈕 -->
            <div class="btn_box" style="margin-bottom: 20px;">
              <button class="btn add" @click="addProgramBlock" :disabled="programBlocks.length >= 4 || isPaid || isSubmitted"
                id="step3">
                {{ getAddButtonText() }}
              </button>
              <div v-if="isPaid" class="notice" style="color: #ff9800; font-weight: bold; margin-top: 10px;">
                ⚠️ The order has been paid, so the enrolled program cannot be modified.
              </div>
            </div>

            <!-- 動態系所選擇區塊 -->
            <div v-if="programBlocks.length > 0" style="width: 100%;">
              <div v-for="(block, blockIndex) in programBlocks" :key="block.id"
                style="border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin-bottom: 20px;">

                <!-- 區塊標題與刪除按鈕 -->
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                  <h4 style="margin: 0; color: #580d63;">Program {{ blockIndex + 1 }}</h4>
                  <button class="btn delete" @click="removeProgramBlock(blockIndex)" :disabled="isPaid || isSubmitted"
                    style="cursor: pointer;">
                    Remove
                  </button>
                </div>

                <!-- 系所選擇下拉選單 -->
                <div class="department" style="margin-bottom: 15px; display: flex; gap: 15px;">
                  <!-- 學位選擇 -->
                  <div :class="['form-group arrow_bg', { active: block.selectedDegree }]" style="flex: 1;">
                    <select class="form-control" :id="'degree_' + (blockIndex + 1)" v-model="block.selectedDegree"
                      @change="onDegreeChange(blockIndex)" :disabled="isPaid || isSubmitted">
                      <option value="" selected></option>
                      <option value="8">Bachelor</option>
                      <option value="10">Master</option>
                      <option value="12">Doctoral</option>
                    </select>
                    <label :for="'degree_' + (blockIndex + 1)"><span class="required">*</span>Select Degree</label>
                    <img src="img/icon_formArrow.svg" alt="" style="top: 33px; right: 28px;" />
                  </div>
                  <!-- <div class="group" style="flex: 1;">
                    <div class="arrow_bg">
                      <select class="form-control" v-model="block.selectedDegree" @change="onDegreeChange(blockIndex)" :disabled="isSubmitted">
                        <option value="">Select Degree</option>
                        <option value="8">Bachelor</option>
                        <option value="10">Master</option>
                        <option value="12">Doctoral</option>
                      </select>
                      <img src="img/icon_formArrow.svg" alt="" />
                    </div>
                  </div> -->

                  <!-- 學院選擇 -->
                  <div :class="['form-group arrow_bg', { active: block.selectedCollege }]" style="flex: 1;">
                    <select class="form-control" :id="'college_' + (blockIndex + 1)" v-model="block.selectedCollege"
                      @change="onCollegeChange(blockIndex)" :disabled="!block.selectedDegree || isPaid || isSubmitted">
                      <option value="" selected></option>
                      <option v-for="college in getFilteredColleges(block.selectedDegree)" :key="college.cId"
                        :value="college.cId">
                        {{ college.name_EN || college.name }}
                      </option>
                    </select>
                    <label :for="'college_' + (blockIndex + 1)"><span class="required">*</span>Select College</label>
                    <img src="img/icon_formArrow.svg" alt="" style="top: 33px; right: 28px;" />
                  </div>
                  <!-- <div class="group" style="flex: 1;">
                    <div class="arrow_bg">
                      <select class="form-control" v-model="block.selectedCollege" @change="onCollegeChange(blockIndex)"
                        :disabled="!block.selectedDegree" :disabled="isSubmitted">
                        <option value="">Select College</option>
                        <option v-for="college in getFilteredColleges(block.selectedDegree)" :key="college.cId"
                          :value="college.cId">
                          {{ college.name_EN || college.name }}
                        </option>
                      </select>
                      <img src="img/icon_formArrow.svg" alt="" />
                    </div>
                  </div> -->

                  <!-- 系所選擇 -->
                  <div :class="['form-group arrow_bg', { active: block.selectedProgram }]" style="flex: 1;">
                    <select class="form-control" :id="'program_' + (blockIndex + 1)" v-model="block.selectedProgram"
                      @change="onProgramChange(blockIndex)" :disabled="!block.selectedCollege || isPaid || isSubmitted">
                      <option value="" selected></option>
                      <option v-for="program in getFilteredPrograms(block.selectedDegree, block.selectedCollege)"
                        :key="program.pId" :value="program.pId">
                        {{ program.name_EN || program.name }}
                      </option>
                    </select>
                    <label :for="'program_' + (blockIndex + 1)"><span class="required">*</span>Select Program</label>
                    <img src="img/icon_formArrow.svg" alt="" style="top: 33px; right: 28px;" />
                  </div>
                  <!-- <div class="group" style="flex: 1;">
                    <div class="arrow_bg">
                      <select class="form-control" v-model="block.selectedProgram" @change="onProgramChange(blockIndex)"
                        :disabled="!block.selectedCollege" :disabled="isSubmitted">
                        <option value="">Select Program</option>
                        <option v-for="program in getFilteredPrograms(block.selectedDegree, block.selectedCollege)"
                          :key="program.pId" :value="program.pId">
                          {{ program.name_EN || program.name }}
                        </option>
                      </select>
                      <img src="img/icon_formArrow.svg" alt="" />
                    </div>
                  </div> -->
                </div>

                <!-- 已選擇的系所資訊 -->
                <div v-if="block.programInfo"
                  style="background: white; padding: 15px; border-radius: 6px; margin-top: 15px;">
                  <div style="margin-bottom: 10px;">
                    <h5 style="margin: 0 0 5px 0; color: #580d63;">{{ block.programInfo.name_EN ||
                      block.programInfo.name }}</h5>
                    <p style="margin: 0; color: #666; font-size: 14px;">
                      {{ block.programInfo.collegeName_EN || block.programInfo.collegeName }} - {{
                        getDegreeText(block.programInfo.degree) }}
                    </p>
                  </div>
                  <div class="text_box" style="background: #f5f5f5; padding: 10px; border-radius: 4px;">
                    <p class="title" style="font-weight: bold; margin-bottom: 5px;">Program Description</p>
                    <p v-if="block.programInfo.extraRequired_EN || block.programInfo.extraRequired" style="margin: 0;">
                      {{ block.programInfo.extraRequired_EN || block.programInfo.extraRequired }}
                    </p>
                    <p v-else style="margin: 0;">
                      Please refer to the program website for detailed requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 未新增區塊時的提示訊息 -->
            <div v-else style="padding: 20px; text-align: center; color: #999;">
              <p>No program blocks added yet. Please click "Add apply program" to start selecting programs.</p>
            </div>
          </div>

          <!-- 獎學金區塊 - 獨立於學程選擇之外 -->
          <div v-if="!isLoading && availableScholarships.length > 0" class="bg" style="margin-top: 20px;">
            <div class="title_box" style="margin-bottom: 20px;">
              <h3>Available Scholarships</h3>
            </div>

            <div style="width: 100%;">
              <div v-for="scholarship in availableScholarships" :key="scholarship.sId"
                style="border: 1px solid rgb(221, 221, 221); border-radius: 8px; padding: 20px; margin-bottom: 20px;">

                <!-- 獎學金標題與說明 -->
                <div style="margin-bottom: 15px;">
                  <h5 style="margin: 0 0 10px 0; color: #580d63; font-weight: 600;">
                    {{ scholarship.name_EN || scholarship.name }}
                  </h5>
                  <div v-if="scholarship.content" v-html="scholarship.content"
                    style="margin-bottom: 10px; color: #666; font-size: 14px; line-height: 1.6;"></div>
                  <a v-if="scholarship.needApplyDoc && scholarship.sampleFile" class="file_a"
                    style="display: inline-block; margin-bottom: 10px; color: #007bff; text-decoration: none; font-weight: 500;"
                    :href="$store.state.src + $store.state.subDirectory + scholarship.sampleFile" target="_blank">
                    📄 Download Sample File
                  </a>
                </div>

                <!-- 是否申請選項 -->
                <div class="check_group" style="margin-bottom: 15px;">
                  <div class="form-check" style="display: inline-block; margin-right: 20px;">
                    <input class="form-check-input" type="radio" :name="'scholarship_' + scholarship.sId"
                      :id="'scholarship_' + scholarship.sId + '_yes'"
                      v-model="scholarshipSelections[scholarship.sId].isApply" :value="true"  :disabled="isSubmitted"/>
                    <label class="form-check-label" :for="'scholarship_' + scholarship.sId + '_yes'">
                      Apply
                    </label>
                  </div>
                  <div class="form-check" style="display: inline-block;">
                    <input class="form-check-input" type="radio" :name="'scholarship_' + scholarship.sId"
                      :id="'scholarship_' + scholarship.sId + '_no'"
                      v-model="scholarshipSelections[scholarship.sId].isApply" :value="false"  :disabled="isSubmitted"/>
                    <label class="form-check-label" :for="'scholarship_' + scholarship.sId + '_no'">
                      Not Apply
                    </label>
                  </div>
                </div>

                <!-- 檔案上傳區塊 -->
                <div v-if="scholarshipSelections[scholarship.sId].isApply && scholarship.needApplyDoc"
                  class="scholarship_upload_box"
                  style="margin-top: 15px; padding: 15px; background: #fff; border: 1px solid #ddd; border-radius: 5px;">

                  <!-- 如果還沒有上傳檔案 -->
                  <div v-if="!scholarshipSelections[scholarship.sId].fileUrl" class="upload_section">
                    <input type="file" :id="'scholarship_file_' + scholarship.sId"
                      @change="onScholarshipFileChange($event, scholarship.sId)" style="display: none;"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"  :disabled="isSubmitted"/>
                    <label :for="'scholarship_file_' + scholarship.sId" class="btn upload_btn"
                      style="cursor: pointer; background: #007bff; color: white; padding: 8px 15px; border-radius: 4px; display: inline-block;">
                      Upload Application Document
                    </label>
                    <p class="notice" style="margin-top: 5px; margin-bottom: 0; font-size: 13px; color: #666;">
                      Please upload your application document (PDF, DOC, DOCX, JPG, PNG, max 10MB)
                    </p>
                  </div>

                  <!-- 如果已經有檔案 -->
                  <div v-else class="uploaded_section">
                    <p style="margin-bottom: 10px; font-weight: bold;"
                      :style="{ color: scholarshipSelections[scholarship.sId].fileUrl.startsWith('data') ? '#dc3545' : '#28a745' }">
                      {{ scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')
                        ? '⏳ Waiting for upload, File name: ' + scholarshipSelections[scholarship.sId].fileName
                        : '✓ Document uploaded successfully' }}
                    </p>
                    <img v-if="scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')"
                      :src="scholarshipSelections[scholarship.sId].fileUrl"
                      style="max-width: 200px; height: auto; border: 1px solid #ddd; border-radius: 4px;" />
                    <div style="display: flex; gap: 10px; align-items: center; margin-top: 10px;">
                      <button v-if="!scholarshipSelections[scholarship.sId].fileUrl.startsWith('data')" type="button"
                        class="btn" @click="downloadScholarshipFile(scholarship.sId)"
                        style="background: #28a745; color: white; padding: 8px 15px; border-radius: 4px; border: none; cursor: pointer;">
                        Download
                      </button>
                      <button type="button" class="btn" @click="removeScholarshipFile(scholarship.sId)"
                        style="background: #dc3545; color: white; padding: 8px 15px; border-radius: 4px; border: none; cursor: pointer;">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 頁面導覽與儲存按鈕 -->
          <div class="btn_box">
            <button class="btn" @click="gotoPrev" :disabled="isSubmitted">Previous</button>
            <button class="btn" @click="gotoNext" :disabled="isSubmitted">Next</button>
            <button class="btn" @click="saveApplication" :disabled="isSaving || isSubmitted">
              {{ isSaving ? "Saving..." : (isSubmitted ? "Submitted (Read-Only)" : "Save") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import applyStudentService from "@/services/applyStudent.js";
import Swal from "sweetalert2";
import { driver } from "driver.js";

export default {
  name: "Form_2",
  components: {
    Header,
    Footer,
  },
  data() {
    return {
      // ========== 進度步驟配置 ==========
      currentStep: 1, // 當前步驟（index 1 = 第二步）
      progressItems: [
        { to: "/Apply/1", title: "Fill out online application form" },
        { to: "/Apply/2", title: "Apply for a program" },
        {
          to: "/Apply/3",
          title:
            "Fill out Recommenders (Do Not Fill Out Without Notifying Your Recommenders in Advance.)",
        },
        { to: "/Apply/4", title: "Upload Required Documents 1" },
        { to: "/Apply/5", title: "Upload Required Documents 2" },
        { to: "/Apply/Check", title: "Review & Submit" },
        { to: "/Apply/Pay", title: "Pay Application Fee" },
      ],

      // ========== 導覽說明配置 ==========
      driverObj: null,
      arr: [
        {
          element: "#step1",
          popover: {
            description: "Important Notice",
            side: "top",
            align: "start",
          },
        },
        {
          element: "#step2",
          popover: {
            description: "List of Available Programs",
            side: "bottom",
            align: "start",
          },
        },
        {
          element: "#step3",
          popover: {
            description: "Add apply program",
            side: "top",
            align: "start",
          },
        },
      ],

      // ========== 載入狀態 ==========
      isLoading: false, // 頁面載入中
      isSaving: false,  // 儲存中

      // ========== 申請狀態 ==========
      isSubmitted: false, // 申請是否已提交

      // ========== API 資料 ==========
      applyInfo: null,              // 申請梯次資訊
      allPrograms: [],              // 所有可申請的系所
      availableColleges: [],        // 所有學院
      availableDepartments: [],     // 所有系所

      // ========== 動態系所選擇區塊 ==========
      programBlocks: [],  // 動態產生的系所選擇區塊
      blockIdCounter: 0,  // 區塊 ID 計數器

      // ========== 步驟內容 ==========
      stepContent: {
        step: 2,
        title: 'Step 2',
        content: '',
        applyInfo: null
      },

      // ========== 訂單資訊 ==========
      asId: null, // 申請表ID
      hasOrder: false, // 是否已建立訂單
      isPaid: false, // 是否已付款 (status === 1)

      // ========== 使用者基本資訊 ==========
      userCountry: '', // 使用者的國籍代碼（用於獎學金篩選）

      // ========== 獎學金相關 ==========
      availableScholarships: [], // 可申請的獎學金清單
      scholarshipSelections: {}, // 獎學金選擇狀態

      // ========== 變動追蹤 ==========
      hasUnsavedChanges: false, // 是否有未儲存的變動
      initialProgramBlocks: [], // 初始載入的系所選擇（用於比對）
      initialScholarshipSelections: {}, // 初始載入的獎學金選擇（用於比對）

      aId: null, // 申請的學期梯次ID
    };
  },

  computed: {
    // 這些 computed 不再需要，因為我們使用 methods 來過濾
  },

  watch: {
    // 監控系所選擇的變動
    programBlocks: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },
    
    // 監控獎學金選擇的變動
    scholarshipSelections: {
      handler() {
        this.checkForChanges();
      },
      deep: true
    },

    // 監控 isSubmitted 狀態並禁用所有表單元素
    isSubmitted: {
      handler(newValue) {
        this.$nextTick(() => {
          this.toggleFormElements(newValue);
        });
      },
      immediate: false
    }
  },

  async mounted() {
    window.scrollTo(0, 0);
    await this.initializeForm();
    
    // 設置路由守衛
    this.setupBeforeUnloadHandler();

    // 初始化時檢查並禁用表單元素（如果已提交）
    this.$nextTick(() => {
      if (this.isSubmitted) {
        this.toggleFormElements(true);
      }
    });
  },

  beforeRouteLeave(to, from, next) {
    // 當要離開此頁面時檢查是否有未儲存的變動
    this.handleRouteLeave(to, next);
  },

  beforeUnmount() {
    // 移除 beforeunload 事件監聽
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },

  methods: {
    // ========== 頁面初始化 ==========

    /**
     * 初始化表單 - 載入所有必要資料
     */
    async initializeForm() {
      this.isLoading = true;
      try {
        const checkResult = await applyStudentService.checkUserApplication();
        if (!checkResult.success || !checkResult.data ||
          !checkResult.data?.isReview ||
          checkResult.data?.isAgree != 1) {
          Swal.fire({
            icon: "warning",
            title: "Eligibility review not completed",
            text: "Please complete the dual citizenship check and agree to the terms and conditions first.",
            confirmButtonText: "OK"
          }).then(() => {
            this.$router.push("/Profile");
          });
          return;
        }

        // 檢查並載入現有申請資料
        await this.loadExistingApplication();

        // 載入步驟內容
        await this.loadStepContent();

        // 載入可申請的系所資訊
        await this.loadActiveApplyPrograms();

        // 載入使用者已選擇的申請系所
        await this.loadUserApplyPrograms();

        // 載入訂單資訊（在取得 asId 後）
        if (this.asId) {
          await this.loadUserOrder();
        }
      } catch (error) {
        console.error("Form initialization error:", error);
        Swal.fire({
          icon: "error",
          title: "Initialization Failed",
          text: "Failed to initialize form. Please refresh the page.",
          confirmButtonText: "OK"
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 載入現有申請資料
     */
    async loadExistingApplication() {
      const result = await applyStudentService.getUserApplication();
      if (result.success && result.data) {
        this.aId = result.data.aId;
      }
    },

    /**
     * 載入步驟內容
     */
    async loadStepContent() {
      const result = await applyStudentService.getActiveApplyStepContent(2, this.aId);
      if (result.success && result.data) {
        this.stepContent = result.data;
        if (!this.stepContent.title) {
          this.stepContent.title = 'Step 2';
        }
      } else {
        // 使用預設值
        this.stepContent = {
          step: 2,
          title: 'Step 2',
          content: '',
          applyInfo: null
        };
      }
    },

    /**
     * 從 API 載入可申請的系所資訊
     */
    async loadActiveApplyPrograms() {
      
      const result = await applyStudentService.getActiveApplyPrograms(this.aId);
      if (result.success && result.data) {
        this.applyInfo = result.data.applyInfo;
        this.allPrograms = result.data.programs || [];
        this.availableColleges = result.data.colleges || [];
        this.availableDepartments = result.data.departments || [];
      } else {
        throw new Error(result.message || "Failed to load programs");
      }
    },

    /**
     * 載入使用者已選擇的申請系所
     */
    async loadUserApplyPrograms() {
      const result = await applyStudentService.getUserApplication();
      console.log("User application data:", result);
      if (result.success && (!result.data || !result.data.hasApplication)) {
        // 顯示請先完成基本資料並跳轉回Apply/1頁面
        Swal.fire({
          icon: "warning",
          title: "Incomplete Basic Information",
          text: "Please complete your basic information first.",
          confirmButtonText: "OK"
        }).then(() => {
          this.$router.push("/Apply/1");
        });
      }
      if (result.success && result.data) {
        // 保存 asId 用於後續載入訂單
        this.asId = result.data.asId;

        // 檢查是否已提交
        this.isSubmitted = result.data.isSubmitted || false;

        // 保存使用者的國籍（用於獎學金篩選）
        this.userCountry = result.data.permCountry || '';

        if (result.data.applyPrograms) {
          try {
            const applyPrograms = JSON.parse(result.data.applyPrograms);

            // 將已選擇的系所轉換為區塊格式
            this.programBlocks = applyPrograms.map((pId) => {
              const program = this.allPrograms.find(p => p.pId === pId);
              if (program) {
                return {
                  id: this.blockIdCounter++,
                  selectedDegree: program.degree.toString(),
                  selectedCollege: program.cId,
                  selectedProgram: program.pId,
                  programInfo: program
                };
              }
              return null;
            }).filter(block => block !== null);

            // 載入所有學程的獎學金
            await this.loadScholarshipsForAllPrograms();
            
            // 延遲儲存初始狀態，確保獎學金資料也載入完成
            this.$nextTick(() => {
              this.saveInitialState();
            });
          } catch (e) {
            console.error("Error parsing apply programs:", e);
          }
        } else {
          // 如果沒有已選擇的系所，也要儲存初始狀態
          this.$nextTick(() => {
            this.saveInitialState();
          });
        }
      } else {
        // 沒有申請資料時也要儲存初始狀態
        this.$nextTick(() => {
          this.saveInitialState();
        });
      }
    },

    /**
     * 載入使用者訂單資訊
     */
    async loadUserOrder() {
      if (!this.asId) {
        console.warn("Cannot load order: asId is not available");
        return;
      }

      try {
        const result = await applyStudentService.hasUserOrder(this.asId);

        if (result.success && result.data) {
          this.hasOrder = result.data.hasOrder;
          this.isPaid = (result.data.status === 1); // status === 1 表示已付款

          console.log("Is paid:", this.isPaid);

          // 如果已付款，顯示通知
          if (this.isPaid) {
            console.log("Payment completed. Add/Remove program buttons will be disabled.");
          }
        } else {
          console.warn("Failed to load order information:", result.message);
          this.hasOrder = false;
          this.isPaid = false;
        }
      } catch (error) {
        console.error("Error loading user order:", error);
        this.hasOrder = false;
        this.isPaid = false;
      }
    },

    /**
     * 儲存初始狀態（用於後續比對變動）
     */
    saveInitialState() {
      // 深拷貝當前的 programBlocks
      this.initialProgramBlocks = JSON.parse(JSON.stringify(this.programBlocks));
      
      // 深拷貝當前的 scholarshipSelections
      this.initialScholarshipSelections = JSON.parse(JSON.stringify(this.scholarshipSelections));
      
      // 重置未儲存變動標記
      this.hasUnsavedChanges = false;
    },

    /**
     * 檢查是否有變動
     */
    checkForChanges() {
      // 比對 programBlocks
      const programsChanged = JSON.stringify(this.programBlocks) !== 
                             JSON.stringify(this.initialProgramBlocks);
      
      // 比對 scholarshipSelections
      const scholarshipsChanged = JSON.stringify(this.scholarshipSelections) !== 
                                 JSON.stringify(this.initialScholarshipSelections);
      
      this.hasUnsavedChanges = programsChanged || scholarshipsChanged;
    },

    /**
     * 設置瀏覽器關閉/重新整理警告
     */
    setupBeforeUnloadHandler() {
      this.handleBeforeUnload = (e) => {
        if (this.hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = '';
          return '';
        }
      };
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    },

    /**
     * 處理路由離開
     */
    async handleRouteLeave(to, next) {
      if (!this.hasUnsavedChanges) {
        next();
        return;
      }

      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. What would you like to do?',
        icon: 'warning',
        //showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Save and Leave',
        denyButtonText: 'Leave without Saving',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#580d63',
        denyButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (result.isConfirmed) {
        // 儲存並離開
        const saveSuccess = await this.saveApplicationSilently();
        if (saveSuccess) {
          this.hasUnsavedChanges = false;
          next();
        } else {
          next(false);
        }
      } else if (result.isDenied) {
        // 直接離開
        this.hasUnsavedChanges = false;
        next();
      } else {
        // 取消
        next(false);
      }
    },

    // ========== UI 互動處理 ==========

    /**
     * 取得進度步驟的 CSS class
     * @param {number} index - 步驟索引
     * @returns {string} CSS class 名稱
     */
    getStepClass(index) {
      if (index < this.currentStep) {
        return "done"; // 已完成
      } else if (index === this.currentStep) {
        return "filling"; // 目前步驟
      }
      return "";
    },

    /**
     * 啟動頁面導覽說明
     */
    startTour() {
      this.driverObj = driver({
        showProgress: true,
        progressText: "{{current}}/{{total}}",
        showButtons: true,
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "End Tour",
        animate: false,
        steps: this.arr,
      });
      this.driverObj.drive();
    },

    // ========== 選擇器變更處理 ==========

    /**
     * 當學位改變時，清除學院和系所的選擇
     */
    onDegreeChange(blockIndex) {
      this.programBlocks[blockIndex].selectedCollege = "";
      this.programBlocks[blockIndex].selectedProgram = "";
      this.programBlocks[blockIndex].programInfo = null;
      // 更新獎學金清單
      this.loadScholarshipsForAllPrograms();
    },

    /**
     * 當學院改變時，清除系所的選擇
     */
    onCollegeChange(blockIndex) {
      this.programBlocks[blockIndex].selectedProgram = "";
      this.programBlocks[blockIndex].programInfo = null;
      // 更新獎學金清單
      this.loadScholarshipsForAllPrograms();
    },

    /**
     * 當系所改變時，更新系所資訊
     */
    async onProgramChange(blockIndex) {
      const block = this.programBlocks[blockIndex];
      const program = this.allPrograms.find(p => p.pId === block.selectedProgram);

      if (block.selectedProgram) {
        if (program) {
          // 檢查是否已經在其他區塊選擇過此系所
          const alreadySelected = this.programBlocks.some((b, idx) =>
            idx !== blockIndex && b.selectedProgram === block.selectedProgram
          );

          if (alreadySelected) {
            Swal.fire({
              icon: "warning",
              title: "Program Already Selected",
              text: "You have already selected this program in another block.",
              confirmButtonText: "OK"
            });
            block.selectedProgram = "";
            block.programInfo = null;
            return;
          }

          block.programInfo = program;
        }
      } else {
        block.programInfo = null;
      }

      // 更新獎學金清單
      await this.loadScholarshipsForAllPrograms();
    },

    /**
     * 根據學位過濾學院
     */
    getFilteredColleges(degree) {
      if (!degree) return [];

      const collegeIds = new Set(
        this.allPrograms
          .filter(p => p.degree === parseInt(degree))
          .map(p => p.cId)
      );

      return this.availableColleges.filter(c => collegeIds.has(c.cId));
    },

    /**
     * 根據學位和學院過濾系所
     */
    getFilteredPrograms(degree, collegeId) {
      if (!collegeId) return [];

      return this.allPrograms.filter(
        p => p.degree === parseInt(degree) && p.cId === collegeId
      );
    },

    // ========== 系所管理 ==========

    /**
     * 新增一個系所選擇區塊
     */
    addProgramBlock() {
      if (this.isPaid) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Add Program",
          text: "The order has been paid, so the enrolled program cannot be modified.",
          confirmButtonText: "OK"
        });
        return;
      }

      if (this.programBlocks.length >= 4) {
        Swal.fire({
          icon: "warning",
          title: "Maximum Programs Reached",
          text: "You can only apply to a maximum of 4 programs.",
          confirmButtonText: "OK"
        });
        return;
      }

      this.programBlocks.push({
        id: this.blockIdCounter++,
        selectedDegree: "",
        selectedCollege: "",
        selectedProgram: "",
        programInfo: null
      });
    },

    /**
     * 移除一個系所選擇區塊
     */
    removeProgramBlock(blockIndex) {
      if (this.isPaid) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Remove Program",
          text: "The order has been paid, so the enrolled program cannot be modified.",
          confirmButtonText: "OK"
        });
        return;
      }

      Swal.fire({
        title: "Remove Program Block?",
        text: "Are you sure you want to remove this program block?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove it",
        cancelButtonText: "Cancel"
      }).then((result) => {
        if (result.isConfirmed) {
          this.programBlocks.splice(blockIndex, 1);
          // 更新獎學金清單
          this.loadScholarshipsForAllPrograms();
        }
      });
    },

    // ========== 輔助方法 ==========

    /**
     * 取得學位的顯示文字
     * @param {number} degree - 學位代碼
     * @returns {string} 學位名稱
     */
    getDegreeText(degree) {
      const degreeMap = {
        8: "Bachelor",
        10: "Master",
        12: "Doctoral"
      };
      return degreeMap[degree] || "";
    },

    /**
     * 取得新增按鈕的顯示文字
     * @returns {string} 按鈕文字
     */
    getAddButtonText() {
      if (this.isPaid) {
        return "The order has been paid, so the enrolled program cannot be add.";
      }
      if (this.programBlocks.length >= 4) {
        return "Maximum 4 programs reached";
      }
      return "Add apply program";
    },

    // ========== 資料儲存 ==========

    /**
     * 儲存申請系所到後端
     */
    async saveApplication() {
      // 檢查是否已提交
      if (this.isSubmitted) {
        Swal.fire({
          icon: "warning",
          title: "Cannot Modify",
          text: "Your application has been submitted and cannot be modified.",
          confirmButtonText: "OK"
        });
        return;
      }

      if (this.isSaving) return;

      // 檢查是否至少選擇一個系所
      // if (this.programBlocks.length === 0) {
      //   Swal.fire({
      //     icon: "warning",
      //     title: "No Programs Selected",
      //     text: "Please add at least one program block before saving.",
      //     confirmButtonText: "OK"
      //   });
      //   return;
      // }

      // 檢查所有區塊是否都有選擇系所
      const incompleteBlocks = this.programBlocks.filter(block => !block.selectedProgram);
      if (incompleteBlocks.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Incomplete Selection",
          text: "Please complete all program selections before saving.",
          confirmButtonText: "OK"
        });
        return;
      }

      // 檢查獎學金文件上傳
      const scholarshipErrors = [];
      this.availableScholarships.forEach(scholarship => {
        const selection = this.scholarshipSelections[scholarship.sId];
        if (selection && selection.isApply && scholarship.needApplyDoc && !selection.fileUrl) {
          scholarshipErrors.push(
            `${scholarship.name_EN} - Application document is required`
          );
        }
      });

      if (scholarshipErrors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Missing Scholarship Documents",
          html: "Please upload required documents:<br><br>" + scholarshipErrors.join("<br>"),
          confirmButtonText: "OK"
        });
        return;
      }

      this.isSaving = true;
      try {
        // 準備申請系所陣列
        const applyPrograms = this.programBlocks
          .filter(block => block.selectedProgram)
          .map(block => block.selectedProgram);

        // 儲存學程選擇
        const result = await applyStudentService.updateApplyPrograms(
          JSON.stringify(applyPrograms),
          JSON.stringify(this.scholarshipSelections)
        );

        if (result.success) {
          // 儲存成功後更新初始狀態
          this.saveInitialState();
          
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: result.data.message || "Application programs and scholarships saved successfully!",
            confirmButtonText: "OK",
            didClose: () => {
              this.loadScholarshipsForAllPrograms();
            }
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Save Failed",
            text: result.message || "Failed to save application programs. Please try again.",
            confirmButtonText: "OK"
          });
        }
      } catch (error) {
        console.error("Save application error:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving. Please try again.",
          confirmButtonText: "OK"
        });
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * 靜默儲存（不顯示成功訊息）
     * @returns {boolean} 儲存是否成功
     */
    async saveApplicationSilently() {
      if (this.isSaving) return false;

      // 檢查所有區塊是否都有選擇系所
      const incompleteBlocks = this.programBlocks.filter(block => !block.selectedProgram);
      if (incompleteBlocks.length > 0) {
        await Swal.fire({
          icon: "warning",
          title: "Incomplete Selection",
          text: "Please complete all program selections before saving.",
          confirmButtonText: "OK"
        });
        return false;
      }

      // 檢查獎學金文件上傳
      const scholarshipErrors = [];
      this.availableScholarships.forEach(scholarship => {
        const selection = this.scholarshipSelections[scholarship.sId];
        if (selection && selection.isApply && scholarship.needApplyDoc && !selection.fileUrl) {
          scholarshipErrors.push(
            `${scholarship.name_EN} - Application document is required`
          );
        }
      });

      if (scholarshipErrors.length > 0) {
        await Swal.fire({
          icon: "warning",
          title: "Missing Scholarship Documents",
          html: "Please upload required documents:<br><br>" + scholarshipErrors.join("<br>"),
          confirmButtonText: "OK"
        });
        return false;
      }

      this.isSaving = true;
      try {
        // 準備申請系所陣列
        const applyPrograms = this.programBlocks
          .filter(block => block.selectedProgram)
          .map(block => block.selectedProgram);

        // 儲存學程選擇
        const result = await applyStudentService.updateApplyPrograms(
          JSON.stringify(applyPrograms),
          JSON.stringify(this.scholarshipSelections)
        );

        if (result.success) {
          // 儲存成功後更新初始狀態
          this.saveInitialState();
          return true;
        } else {
          await Swal.fire({
            icon: "error",
            title: "Save Failed",
            text: result.message || "Failed to save application programs. Please try again.",
            confirmButtonText: "OK"
          });
          return false;
        }
      } catch (error) {
        console.error("Save application error:", error);
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving. Please try again.",
          confirmButtonText: "OK"
        });
        return false;
      } finally {
        this.isSaving = false;
      }
    },

    // ========== 頁面導覽 ==========

    /**
     * 回到上一頁（Form 1）
     */
    gotoPrev() {
      this.$router.push("/Apply/1").then(() => {
        window.scrollTo(0, 0);
      });
    },

    /**
     * 前往下一頁（Form 3）
     */
    gotoNext() {
      this.$router.push("/Apply/3").then(() => {
        window.scrollTo(0, 0);
      });
    },

    // ========== 獎學金相關方法 ==========

    /**
     * 載入所有選定學程的獎學金清單
     */
    async loadScholarshipsForAllPrograms() {
      if (!this.userCountry) {
        console.warn("User country not available, skipping scholarship load");
        this.availableScholarships = [];
        this.scholarshipSelections = {};
        return;
      }

      // 取得所有已選擇的學程 ID
      const selectedProgramIds = this.programBlocks
        .filter(block => block.selectedProgram)
        .map(block => block.selectedProgram);

      if (selectedProgramIds.length === 0) {
        this.availableScholarships = [];
        this.scholarshipSelections = {};
        return;
      }

      try {
        const result = await applyStudentService.getAvailableScholarshipsByProgram(
          this.userCountry,
          selectedProgramIds
        );

        if (result.success && result.data) {
          this.availableScholarships = result.data;

          // 初始化獎學金選擇狀態（保留已有的選擇）
          const newScholarshipSelections = {};
          result.data.forEach(scholarship => {
            // 如果已經有選擇記錄，保留它；否則使用伺服器返回的資料或預設值
            // if (this.scholarshipSelections[scholarship.sId]) {
            //   newScholarshipSelections[scholarship.sId] = this.scholarshipSelections[scholarship.sId];
            // } else {
            //   newScholarshipSelections[scholarship.sId] = {
            //     isApply: scholarship.applyStudentScholarship?.isApply || false,
            //     fileUrl: scholarship.applyStudentScholarship?.url || '',
            //     fileName: ''
            //   };
            // }
            newScholarshipSelections[scholarship.sId] = {
              isApply: scholarship.applyStudentScholarship?.isApply || false,
              fileUrl: scholarship.applyStudentScholarship?.url || '',
              fileName: ''
            };
          });
          this.scholarshipSelections = newScholarshipSelections;
        } else {
          this.availableScholarships = [];
          this.scholarshipSelections = {};
        }
      } catch (error) {
        console.error("Error loading scholarships:", error);
        this.availableScholarships = [];
        this.scholarshipSelections = {};
      }
    },

    /**
     * 處理獎學金文件上傳
     * @param {Event} event 檔案選擇事件
     * @param {number} scholarshipId 獎學金ID
     */
    async onScholarshipFileChange(event, scholarshipId) {
      const file = event.target.files[0];
      if (!file) return;

      // 檢查檔案類型
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png'
      ];

      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select a valid file (PDF, DOC, DOCX, JPG, PNG)',
          confirmButtonText: 'OK'
        });
        event.target.value = "";
        return;
      }

      // 檢查檔案大小 (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'The file size exceeds 10MB. Please select a smaller file.',
          confirmButtonText: 'OK'
        });
        event.target.value = "";
        return;
      }

      // 讀取檔案並轉換為 Base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result;

        // 儲存到 scholarshipSelections
        this.scholarshipSelections[scholarshipId].fileUrl = base64Data;
        this.scholarshipSelections[scholarshipId].fileName = file.name;

        Swal.fire({
          icon: 'success',
          title: 'File Uploaded',
          text: 'File uploaded successfully. Please remember to save the form.',
          confirmButtonText: 'OK',
          timer: 2000
        });
      };

      reader.onerror = () => {
        Swal.fire({
          icon: 'error',
          title: 'File Read Error',
          text: 'Failed to read the file. Please try again.',
          confirmButtonText: 'OK'
        });
        event.target.value = "";
      };

      reader.readAsDataURL(file);
    },

    /**
     * 下載獎學金文件
     * @param {number} scholarshipId 獎學金ID
     */
    downloadScholarshipFile(scholarshipId) {
      const fileUrl = this.scholarshipSelections[scholarshipId].fileUrl;

      if (!fileUrl) {
        Swal.fire({
          icon: 'error',
          title: 'No File',
          text: 'No file available to download.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // 如果是 base64 資料，直接下載
      if (fileUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = this.scholarshipSelections[scholarshipId].fileName ||
          `scholarship_${scholarshipId}_document`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // 如果是伺服器路徑，在新視窗打開
        window.open(
          this.$store.state.src + this.$store.state.subDirectory + fileUrl,
          '_blank'
        );
      }
    },

    /**
     * 移除獎學金文件
     * @param {number} scholarshipId 獎學金ID
     */
    async removeScholarshipFile(scholarshipId) {
      const confirmResult = await Swal.fire({
        title: 'Remove Document?',
        text: 'Are you sure you want to remove this document?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      });

      if (confirmResult.isConfirmed) {
        // 清除檔案資料
        this.scholarshipSelections[scholarshipId].fileUrl = '';
        this.scholarshipSelections[scholarshipId].fileName = '';

        // 清除檔案輸入框
        const fileInput = document.getElementById(
          `scholarship_file_${scholarshipId}`
        );
        if (fileInput) {
          fileInput.value = "";
        }

        Swal.fire({
          icon: 'success',
          title: 'Removed',
          text: 'Document has been removed. Remember to save the form.',
          confirmButtonText: 'OK',
          timer: 2000
        });
      }
    },

    /**
     * 切換表單元素的禁用狀態
     * @param {boolean} disable 是否禁用
     */
    toggleFormElements(disable) {
      // 使用 setTimeout 確保 DOM 已完全渲染
      setTimeout(() => {
        const container = document.querySelector('.box');
        if (!container) return;

        // 禁用所有 input (包含 text, radio, checkbox, file 等)
        const inputs = container.querySelectorAll('input:not(.tours_btn)');
        inputs.forEach(input => {
          input.disabled = disable;
        });

        // 禁用所有 select
        const selects = container.querySelectorAll('select');
        selects.forEach(select => {
          select.disabled = disable;
        });

        // 禁用所有 textarea
        const textareas = container.querySelectorAll('textarea');
        textareas.forEach(textarea => {
          textarea.disabled = disable;
        });

        // 禁用所有按鈕 (除了使用說明按鈕)
        const buttons = container.querySelectorAll('button:not(.tours_btn):not(.head-btn)');
        buttons.forEach(button => {
          button.disabled = disable;
        });
      }, 100);
    },
  },
};
</script>

<style scoped>
/* 當表單已提交時，禁用所有輸入元素 */
.submitted-readonly input,
.submitted-readonly select,
.submitted-readonly textarea,
.submitted-readonly button:not(.tours_btn) {
  pointer-events: none !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.submitted-readonly .form-check-input,
.submitted-readonly input[type="radio"],
.submitted-readonly input[type="checkbox"] {
  pointer-events: none !important;
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

.submitted-readonly .form-check-label {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}
</style>
